import React, { useState, useEffect } from "react";
import styles from "./kpi-card.module.css";
import { kpiApi } from "../../../api/home/kpiApi";

const KPICard = () => {
  const [dateFilter, setDateFilter] = useState('thisMonth');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [metrics, setMetrics] = useState({
    totalSales: 0,
    lines: 0,
    orderCount: 0,
    orderAmount: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Calculate default dates
  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setFromDate(formatDateForInput(firstDayOfMonth));
    setToDate(formatDateForInput(lastDayOfMonth));
  }, []);

  // Fetch metrics when dates change
  useEffect(() => {
    if (fromDate && toDate) {
      fetchMetrics();
    }
  }, [fromDate, toDate]);

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    const today = new Date();
    
    switch(filter) {
      case 'thisWeek':
        const firstDayOfWeek = new Date(today);
        firstDayOfWeek.setDate(today.getDate() - today.getDay());
        setFromDate(formatDateForInput(firstDayOfWeek));
        setToDate(formatDateForInput(today));
        break;
        
      case 'thisMonth':
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        setFromDate(formatDateForInput(firstDayOfMonth));
        setToDate(formatDateForInput(lastDayOfMonth));
        break;
        
      case 'custom':
        // Keep current dates
        break;
        
      default:
        break;
    }
  };

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📊 Fetching KPI metrics...', { fromDate, toDate });
      
      const data = await kpiApi.getKPIMetrics(fromDate, toDate);
      
      console.log('✅ KPI metrics received:', data);
      
      setMetrics({
        totalSales: data.totalSales || 0,
        lines: data.lines || 0,
        orderCount: data.orderHeaderCount || 0,
        orderAmount: data.orderAmount || 0
      });
    } catch (err) {
      setError('Failed to load metrics');
      console.error('❌ Error fetching KPI metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return '₹' + (amount || 0).toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const formatNumber = (num) => {
    return (num || 0).toLocaleString('en-IN');
  };

  return (
    <div className={styles.componentWrapper}>
      <h2 className={styles.componentTitle}>Key Performance Indicators</h2>
      
      {/* Date Filter Section */}
      <div className={styles.filterSection}>
        <div className={styles.filterButtons}>
          <button
            className={`${styles.filterButton} ${dateFilter === 'thisWeek' ? styles.filterButtonActive : ''}`}
            onClick={() => handleDateFilterChange('thisWeek')}
          >
            This Week
          </button>
          <button
            className={`${styles.filterButton} ${dateFilter === 'thisMonth' ? styles.filterButtonActive : ''}`}
            onClick={() => handleDateFilterChange('thisMonth')}
          >
            This Month
          </button>
          <button
            className={`${styles.filterButton} ${dateFilter === 'custom' ? styles.filterButtonActive : ''}`}
            onClick={() => handleDateFilterChange('custom')}
          >
            Custom
          </button>
        </div>
        
        {dateFilter === 'custom' && (
          <div className={styles.customDateInputs}>
            <div className={styles.dateInputGroup}>
              <label className={styles.dateLabel}>From:</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={styles.dateInput}
              />
            </div>
            <div className={styles.dateInputGroup}>
              <label className={styles.dateLabel}>To:</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={styles.dateInput}
              />
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className={styles.errorMessage}>{error}</div>
      )}

      {/* Loading State */}
      {loading && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <span className={styles.loadingText}>Loading metrics...</span>
        </div>
      )}

      {/* Metrics Cards */}
      {!loading && (
        <div className={styles.metricsGrid}>
          {/* Total Sales Card */}
          <div className={styles.metricCard}>
            <div className={styles.metricIcon} style={{ background: '#dbeafe' }}>
               <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
              {/* <span style={{ fontSize: '24px' }}>💰</span> */}
            </div>
            <div className={styles.metricContent}>
              <div className={styles.metricLabel}>Total Sales</div>
              <div className={styles.metricValue}>{formatCurrency(metrics.totalSales)}</div>
            </div>
          </div>

          {/* Lines Card */}
          <div className={styles.metricCard}>
            <div className={styles.metricIcon} style={{ background: '#dcfce7' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6"/>
                <line x1="8" y1="12" x2="21" y2="12"/>
                <line x1="8" y1="18" x2="21" y2="18"/>
                <line x1="3" y1="6" x2="3.01" y2="6"/>
                <line x1="3" y1="12" x2="3.01" y2="12"/>
                <line x1="3" y1="18" x2="3.01" y2="18"/>
              </svg>
              {/* <span style={{ fontSize: '24px' }}>📋</span> */}
            </div>
            <div className={styles.metricContent}>
              <div className={styles.metricLabel}>Lines</div>
              <div className={styles.metricValue}>{formatNumber(metrics.lines)}</div>
            </div>
          </div>

          {/* Orders Card */}
          <div className={styles.metricCard}>
            <div className={styles.metricIcon} style={{ background: '#fef3c7' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 0 1-8 0"/>
              </svg>
              {/* <span style={{ fontSize: '24px' }}>📦</span> */}
            </div>
            <div className={styles.metricContent}>
              <div className={styles.metricLabel}>Orders</div>
              <div className={styles.metricValue}>{formatNumber(metrics.orderCount)}</div>
            </div>
          </div>

          {/* Order Value Card */}
          <div className={styles.metricCard}>
            <div className={styles.metricIcon} style={{ background: '#fce7f3' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#db2777" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                <line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
              {/* <span style={{ fontSize: '24px' }}>💵</span> */}
            </div>
            <div className={styles.metricContent}>
              <div className={styles.metricLabel}>Order Value</div>
              <div className={styles.metricValue}>{formatCurrency(metrics.orderAmount)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPICard;